import React, { useState, useEffect, useCallback, useRef } from "react"
import { connect } from "react-redux"
import styles from "./WorkOfferGrid.module.scss"
import {
    MapPin,
    Calendar,
    DollarSign,
    Clock,
    Send,
    AlertCircle,
    Building2,
    UserRoundPlus,
    Mail,
    HardHat,
    ChevronLeft,
    ChevronRight,
} from "lucide-react"
import { fireAnalytics } from "../../utils/firebase"
import { useNavigate } from "react-router-dom"
import WorkApplyModal from "../../pages/WorkBag/WorkApplyModal"
import { getAllWorkOffers, openMainModal, closeMainModal } from "../../store/actions"

interface WorkOfferData {
    id: string
    user: {
        username: string
        image_profile: string
        type: string
        razon_social?: string
        name?: string
        last_name?: string
    }
    skill: Array<{
        skill: {
            name: string
        }
    }>
    workZones: Array<{
        jurisdiccion: {
            name: string
        }
        city: string
    }>
    startDate: string
    activeUntil: string
    salary: string
    salaryType: string
    immediateStart: boolean
    description: string
    createdAt: string
}

interface WorkOfferGridProps {
    allJobRequests: any
    getAllWorkOffers: () => void
    scrollToForm: () => void
    userData?: any
    openMainModal: (config: any) => void
    closeMainModal: () => void
}

const WorkOfferCard: React.FC<{ 
    workOffer: WorkOfferData; 
    handleApply: (offer: WorkOfferData, isExternal: boolean) => void 
}> = ({ workOffer, handleApply }) => {
    const [passedStartDate, setPassedStartDate] = useState(false)
    const [formattedSalary, setFormattedSalary] = useState("")
    const [isExternalOffer, setIsExternalOffer] = useState(false)

    useEffect(() => {
        if (workOffer?.user?.username) {
            const isExternal = workOffer.user?.username.toLowerCase() === "gonzalo@edify.la"
            setIsExternalOffer(isExternal)
        }
    }, [workOffer])


    useEffect(() => {
        const today = new Date()
        if (workOffer?.startDate && new Date(workOffer.startDate) < today) {
            setPassedStartDate(true)
        }
    }, [workOffer])

    useEffect(() => {
        const salary = workOffer?.salary ? Math.floor(Number.parseFloat(workOffer?.salary)) : "0"
        if (workOffer?.salary === "0.00" || salary === "0") {
            setFormattedSalary("A convenir")
        } else if (workOffer?.salaryType === "Hour") {
            setFormattedSalary(`${salary}/h`)
        } else if (workOffer?.salaryType === "Day") {
            setFormattedSalary(`${salary}/día`)
        } else if (workOffer?.salaryType === "Week") {
            setFormattedSalary(`${salary}/sem`)
        } else if (workOffer?.salaryType === "Month") {
            setFormattedSalary(`${salary}/mes`)
        } else if (workOffer?.salaryType === "Job") {
            setFormattedSalary(`${salary}/proy`)
        } else {
            setFormattedSalary(`${salary}`)
        }
    }, [workOffer])

    const calcularDiferenciaTiempo = (fechaCreacion: string) => {
        const fechaActual = new Date()
        const fechaCreacionUTC = new Date(fechaCreacion)

        let diferencia = fechaActual.getTime() - fechaCreacionUTC.getTime()

        if (diferencia < 0) {
            diferencia = 0
        }

        const minutos = Math.floor(diferencia / (1000 * 60))
        const horas = Math.floor(diferencia / (1000 * 60 * 60))
        const dias = Math.floor(diferencia / (1000 * 60 * 60 * 24))

        if (minutos < 1) {
            return "Ahora"
        } else if (minutos < 60) {
            return `${minutos}m`
        } else if (horas < 24) {
            return `${horas}h`
        } else if (dias < 7) {
            return `${dias}d`
        } else {
            return `${Math.floor(dias / 7)}sem`
        }
    }

    const formatStartDate = (date: string) => {
        const day = date?.split("T")[0].split("-")[2]
        const month = date?.split("T")[0].split("-")[1]
        return `${day}/${month}`
    }


    return (
        <div className={`${styles.workOffer_card} ${isExternalOffer ? styles.workOffer_card_external : ""}`}>
            <div className={styles.workOffer_header}>
                <div className={styles.workOffer_companyInfo}>
                    <img
                        src={workOffer?.user?.image_profile || "/placeholder.svg"}
                        alt="Empresa"
                        className={styles.workOffer_companyInfo_avatar}
                    />
                    <div className={styles.workOffer_companyInfo_details}>
                        <h3 className={styles.workOffer_companyInfo_details_name}>
                            {workOffer?.user?.type === "COMPANY"
                                ? workOffer?.user?.razon_social
                                : `${workOffer?.user?.name} ${workOffer?.user?.last_name}`}
                            {isExternalOffer && (
                                <span className={styles.externalBadge}>Búsqueda externa</span>
                            )}
                        </h3>
                        <p className={styles.workOffer_companyInfo_details_time}>
                            {calcularDiferenciaTiempo(workOffer?.createdAt)}
                        </p>
                    </div>
                </div>
            </div>

            <div className={styles.workOffer_content}>
                <div className={styles.workOffer_skills}>
                    <div className={styles.workOffer_skills_list}>
                        {workOffer?.skill?.slice(0, 2).map((skill, index) => (
                            <span key={index} className={styles.workOffer_skills_list_item}>
                                {skill?.skill?.name}
                            </span>
                        ))}
                        {workOffer?.skill?.length > 2 && (
                            <span className={styles.workOffer_skills_list_item}>
                                +{workOffer.skill.length - 2}
                            </span>
                        )}
                    </div>
                </div>

                {workOffer?.description && (
                    <div className={styles.workOffer_description}>
                        <p className={styles.workOffer_description_text}>
                            {workOffer.description}
                        </p>
                    </div>
                )}

                <div className={styles.workOffer_details}>
                    <div className={styles.workOffer_details_item}>
                        <MapPin size={14} />
                        <span>{workOffer?.workZones?.[0]?.jurisdiccion?.name}</span>
                    </div>

                    <div className={styles.workOffer_details_item}>
                        <Calendar size={14} />
                        <span>
                            {workOffer?.immediateStart || passedStartDate
                                ? "Inmediato"
                                : formatStartDate(workOffer?.startDate)}
                        </span>
                    </div>

                    <div className={styles.workOffer_details_item}>
                        <DollarSign size={14} />
                        <span>{formattedSalary}</span>
                    </div>

                    <div className={styles.workOffer_details_item}>
                        <Clock size={14} />
                        <span>Hasta {formatStartDate(workOffer?.activeUntil)}</span>
                    </div>
                </div>
            </div>

            <div className={styles.workOffer_footer}>
                <button
                    className={styles.workOffer_applyButton}
                    onClick={() => handleApply(workOffer, isExternalOffer)}
                >
                    <Send size={16} />
                    Aplicar
                </button>
            </div>
        </div>
    )
}

const CARDS_PER_VIEW_DESKTOP = 3
const CARDS_PER_VIEW_MOBILE = 1

const WorkOfferGrid: React.FC<WorkOfferGridProps> = React.memo(({ allJobRequests, getAllWorkOffers, scrollToForm, userData, openMainModal, closeMainModal }) => {
    const [showExternalApplyModal, setShowExternalApplyModal] = useState(false)
    const [selectedOffer, setSelectedOffer] = useState<WorkOfferData | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [hasError, setHasError] = useState(false)
    const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)
    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" && window.innerWidth <= 900
    )
    const [currentSlide, setCurrentSlide] = useState(0)
    const viewportRef = useRef<HTMLDivElement>(null)
    const slideRefs = useRef<(HTMLDivElement | null)[]>([])
    const navigate = useNavigate()
    const total = allJobRequests?.workOffers?.length ?? 0
    const cardsPerView = isMobile ? CARDS_PER_VIEW_MOBILE : CARDS_PER_VIEW_DESKTOP
    const totalSlides = Math.ceil(total / cardsPerView) || 1

    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth <= 900)
        window.addEventListener("resize", handler)
        return () => window.removeEventListener("resize", handler)
    }, [])

    useEffect(() => {
        setCurrentSlide(0)
    }, [allJobRequests?.workOffers?.length, isMobile])

    const handleScroll = useCallback(() => {
        const viewport = viewportRef.current
        if (!viewport || total === 0) return
        const scrollLeft = viewport.scrollLeft
        let closest = 0
        let minDist = Infinity
        for (let i = 0; i < totalSlides; i++) {
            const slide = slideRefs.current[i]
            if (!slide) continue
            const dist = Math.abs(slide.offsetLeft - scrollLeft)
            if (dist < minDist) {
                minDist = dist
                closest = i
            }
        }
        setCurrentSlide(closest)
    }, [total, totalSlides])

    useEffect(() => {
        const el = viewportRef.current
        if (!el) return
        el.addEventListener("scroll", handleScroll, { passive: true })
        return () => el.removeEventListener("scroll", handleScroll)
    }, [handleScroll])

    // Función para actualizar ofertas manualmente
    const handleRefresh = useCallback(() => {
        setIsLoading(true)
        setHasError(false)
        getAllWorkOffers()
    }, [getAllWorkOffers])

    // Carga inicial forzada si no hay datos
    useEffect(() => {
        if (!allJobRequests && !hasInitiallyLoaded) {
            setIsLoading(true)
            getAllWorkOffers()
        }
    }, [allJobRequests, hasInitiallyLoaded, getAllWorkOffers]) // Solo se ejecuta una vez al montar

    // Auto-refresh cuando el usuario vuelve a la pestaña (opcional)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && hasInitiallyLoaded) {
                // Solo actualizar si han pasado al menos 30 segundos desde la última carga
                const lastUpdate = localStorage.getItem('workOffersLastUpdate')
                const now = Date.now()
                if (!lastUpdate || (now - parseInt(lastUpdate)) > 30000) {
                    handleRefresh()
                    localStorage.setItem('workOffersLastUpdate', now.toString())
                }
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
    }, [hasInitiallyLoaded, handleRefresh])

    // Cargar ofertas solo si no están cargadas y no hay error
    useEffect(() => {
        // Verificar si no hay datos cargados (undefined, null, o sin workOffers)
        const hasNoData = !allJobRequests || 
                         !allJobRequests.workOffers || 
                         allJobRequests.workOffers.length === 0
        
        if (hasNoData && !hasError && !isLoading && !hasInitiallyLoaded) {
            setIsLoading(true)
            setHasError(false)
            
            getAllWorkOffers()
        }
    }, [allJobRequests, hasError, isLoading, hasInitiallyLoaded, getAllWorkOffers])

    // Manejar cuando las ofertas se cargan exitosamente o con error
    useEffect(() => {
        if (allJobRequests !== undefined && allJobRequests !== null) {
            setIsLoading(false)
            
            // Verificar si hay error en el payload
            if (allJobRequests.error) {
                setHasError(true)
            } else {
                setHasError(false)
                setHasInitiallyLoaded(true)
                // Guardar timestamp de la última actualización exitosa
                localStorage.setItem('workOffersLastUpdate', Date.now().toString())
            }
        }
    }, [allJobRequests])

    // Función para reintentar la carga
    const handleRetry = () => {
        setHasError(false)
        setIsLoading(true)
        getAllWorkOffers()
    }

    const handleApply = (offer: WorkOfferData, isExternalOffer: boolean) => {
        fireAnalytics("apply_work_offer", { category: "work_offer" })
        if (isExternalOffer) {
            setShowExternalApplyModal(true)
            setSelectedOffer(offer)
        } else {
            const isEnrolled = Boolean(userData)
            if (!isEnrolled) {
                openMainModal({
                    title: "Inscribite para aplicar",
                    body: "Para postularte a esta oferta, primero debes inscribirte en la bolsa de trabajo.",
                    iconType: "warning",
                    isSuccess: true,
                    noLabel: "Inscribirme",
                    customCloseMainModal: () => {
                        closeMainModal()
                        scrollToForm()
                    },
                })
            } else {
                navigate(`/bolsadetrabajo/${offer.id}`)
            }
        }
    }

    // Estado de carga inicial
    if (isLoading && !hasInitiallyLoaded) {
        return (
            <div className={styles.loadingState}>
                <div className={styles.loadingSpinner}></div>
                <p>Cargando ofertas...</p>
            </div>
        )
    }

    // Estado de error
    if (hasError) {
        return (
            <div className={styles.errorState}>
                <div className={styles.errorState_icon}>
                    <AlertCircle size={48} />
                </div>
                <h2 className={styles.errorState_title}>No pudimos cargar las ofertas</h2>
                <p className={styles.errorState_description}>
                    Ocurrió un problema al cargar las ofertas de trabajo. Por favor intenta nuevamente.
                </p>
                <button className={styles.retryButton} onClick={handleRetry}>
                    <AlertCircle size={18} />
                    Reintentar
                </button>
            </div>
        )
    }

    // Estado vacío (sin ofertas)
    if (allJobRequests?.total === 0) {
        return (
            <div className={styles.emptyState}>
                <div className={styles.emptyState_icon}>
                    <HardHat size={28} />
                </div>
                <h2 className={styles.emptyState_title}>Estamos construyendo las mejores oportunidades</h2>
                <p className={styles.emptyState_description}>
                    Próximamente encontrarás ofertas laborales exclusivas en el sector de la construcción.
                </p>
                <ul className={styles.emptyState_list}>
                    <li className={styles.emptyState_list_item}>
                        <HardHat className={styles.emptyState_list_item_icon} />
                        <div>
                            <strong>Ofertas exclusivas</strong> en construcción
                        </div>
                    </li>
                    <li className={styles.emptyState_list_item}>
                        <UserRoundPlus className={styles.emptyState_list_item_icon} />
                        <div>
                            <strong>Inscribite gratis</strong> y sé el primero
                        </div>
                    </li>
                    <li className={styles.emptyState_list_item}>
                        <Building2 className={styles.emptyState_list_item_icon} />
                        <div>
                            Conectamos con las <strong>mejores empresas</strong>
                        </div>
                    </li>
                    <li className={styles.emptyState_list_item}>
                        <Mail className={styles.emptyState_list_item_icon} />
                        <div>
                            ¿Dudas? <a href="mailto:contacto@edify.la">contacto@edify.la</a>
                        </div>
                    </li>
                </ul>
                <button className={styles.switchButton} onClick={scrollToForm}>
                    <UserRoundPlus size={18} />
                    Inscribirme ahora
                </button>
            </div>
        )
    }

    // Si no hay ofertas pero sí hay datos cargados
    if (!allJobRequests?.workOffers || allJobRequests.workOffers.length === 0) {
        return (
            <div className={styles.emptyState}>
                <div className={styles.emptyState_icon}>
                    <HardHat size={28} />
                </div>
                <h2 className={styles.emptyState_title}>No hay ofertas disponibles</h2>
                <p className={styles.emptyState_description}>
                    En este momento no hay ofertas de trabajo disponibles. Vuelve a revisar más tarde.
                </p>
                <button className={styles.switchButton} onClick={scrollToForm}>
                    <UserRoundPlus size={18} />
                    Inscribirme ahora
                </button>
            </div>
        )
    }

    const workOffers = allJobRequests.workOffers.slice().reverse()
    const slideIndex = Math.min(currentSlide, totalSlides - 1)

    const slides: WorkOfferData[][] = []
    for (let i = 0; i < total; i += cardsPerView) {
        slides.push(workOffers.slice(i, i + cardsPerView))
    }
    if (slides.length === 0) slides.push([])

    const startIdx = slideIndex * cardsPerView
    const endIdx = Math.min(startIdx + cardsPerView, total)
    const countText =
        total > 0
            ? isMobile
                ? `Mostrando ${startIdx + 1} de ${total}`
                : `Mostrando ${startIdx + 1}–${endIdx} de ${total} ofertas`
            : `${total} ofertas`

    const canPrev = slideIndex > 0
    const canNext = slideIndex < totalSlides - 1

    const goPrev = () => {
        const targetIndex = Math.max(0, slideIndex - 1)
        slideRefs.current[targetIndex]?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "start",
        })
    }

    const goNext = () => {
        const targetIndex = Math.min(totalSlides - 1, slideIndex + 1)
        slideRefs.current[targetIndex]?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "start",
        })
    }

    return (
        <div className={styles.workOfferGrid}>
            <div className={styles.carouselHeader}>
                <p className={styles.carouselCount}>{countText}</p>
                <div className={styles.carouselArrows}>
                    <button
                        type="button"
                        className={styles.carouselArrow}
                        onClick={goPrev}
                        disabled={!canPrev}
                        aria-label="Anterior"
                    >
                        <ChevronLeft size={24} />
                    </button>
                    <button
                        type="button"
                        className={styles.carouselArrow}
                        onClick={goNext}
                        disabled={!canNext}
                        aria-label="Siguiente"
                    >
                        <ChevronRight size={24} />
                    </button>
                </div>
            </div>

            <div
                ref={viewportRef}
                className={`${styles.carouselViewport} ${styles.carouselViewport_scroll}`}
            >
                <div className={styles.carouselTrack}>
                    {slides.map((slideOffers, idx) => (
                        <div
                            key={idx}
                            ref={(el) => {
                                slideRefs.current[idx] = el
                            }}
                            className={styles.carouselSlide}
                        >
                            {slideOffers.map((workOffer: WorkOfferData) => (
                                <WorkOfferCard
                                    key={workOffer.id}
                                    workOffer={workOffer}
                                    handleApply={handleApply}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {showExternalApplyModal && (
                <WorkApplyModal
                    workOffer={selectedOffer}
                    setShowApplyModal={setShowExternalApplyModal}
                    setShowForm={scrollToForm}
                    forbiddenClose={false}
                    prefilledEmail={undefined}
                    identityValidated={undefined}
                    userData={userData}
                />
            )}
        </div>
    )
})

const mapStateToProps = (state: any) => {
    return {
        allJobRequests: state.register.allJobRequests,
        userData: state.general?.userData,
    }
}

export default connect(mapStateToProps, { getAllWorkOffers, openMainModal, closeMainModal })(WorkOfferGrid)
